import api from './api';
import { ProjectManifest, ArtifactoryNode, NodeStatus, HandoffPlugin, ProjectAsset } from '../types/artifactory';
import { useArtifactoryStore } from '../stores/artifactoryStore';

// Session-owned genesis poll shape (GET …/blueprint/status). `expected_count` is a rough
// N/~T target while germinating (estimate_genesis floor), null once landed.
export interface BlueprintLiveStatus {
  bound: boolean;
  manifest_id?: string;
  germinating?: boolean;
  genesis_failed?: boolean;
  genesis_error?: string | null;
  seedling?: boolean;
  node_count?: number;
  expected_count?: number | null;
}

// Genesis WS events (connection_manager → /ws/sessions/{id}); payload lives in msg.data.
// Field names must match backend run_genesis / create_message exactly.
export interface GenesisStartedEvent { manifest_id: string; }
export interface GenesisNodeLandedEvent { manifest_id: string; index: number; path: string; persona: string; }
export interface GenesisCompleteEvent { manifest_id: string; node_count: number; }

export const artifactoryService = {
  setManifestAssets: async (manifestId: string, assets: ProjectAsset[]): Promise<ProjectAsset[]> => {
    const r = await api.put(`/artifactory/manifest/${manifestId}/assets`, assets);
    return r.data as ProjectAsset[];
  },

  scaffoldProject(sessionId, intent, rootPath = "/", personaId = null) {
    return api.post(`/artifactory/scaffold`, {
      session_id: sessionId,
      intent,
      root_path: rootPath,
      persona_id: personaId
    }).then(response => response.data);
  },
  
  listArtifactoryProjects: async (): Promise<any[]> => {
    const response = await api.get('/artifactory/projects');
    // Ensure manifest_content is properly handled
    return response.data.map((project: any) => {
      if (project.manifest_content && typeof project.manifest_content === 'string') {
        try {
          project.manifest_content = JSON.parse(project.manifest_content);
        } catch (e) {
          console.warn('Failed to parse manifest_content as JSON', e);
        }
      }
      return project;
    });
  },

  getManifest: async (manifestId: string): Promise<ProjectManifest> => {
    const response = await api.get(`/artifactory/manifest/${manifestId}`);
    return response.data;
  },

  updateNode: async (manifestId: string, nodeId: string, updates: Partial<ArtifactoryNode>): Promise<ArtifactoryNode> => {
    const response = await api.patch(`/artifactory/manifest/${manifestId}/nodes/${nodeId}`, updates);
    return response.data;
  },

  hydrateNode: async (manifestId: string, nodeId: string, sessionId: string, withDependencies: boolean = false): Promise<any> => {
    // console.log(`Hydrating node ${nodeId} in manifest ${manifestId} ${withDependencies ? 'WITH' : 'WITHOUT'} dependencies`);
    // console.log(`withDependencies parameter type: ${typeof withDependencies}, value: ${withDependencies}`);
    
    // Ensure withDependencies is a boolean
    const boolWithDependencies = Boolean(withDependencies);
    
    const payload = {
      manifest_id: manifestId,
      session_id: sessionId,
      with_dependencies: boolWithDependencies
    };
    // console.log('Request payload:', payload);
    // console.log('JSON.stringify(payload):', JSON.stringify(payload));
    
    const response = await api.post(`/artifactory/hydrate/${nodeId}`, payload);
    // console.log(`Hydration response:`, response.data);
    return response.data;
  },

  hydrateNodeWithStatus: async (manifestId: string, nodeIdOrPath: string, sessionId: string, withDependencies: boolean = false): Promise<any> => {
    // First update local state to show hydrating status
    try {
      // Access the store using getState
      const store = useArtifactoryStore.getState();
      
      // Check if this is a path or a node ID
      const isPath = nodeIdOrPath.includes('/');
      
      // If it's a path, try to find the corresponding node ID
      let effectiveNodeId = nodeIdOrPath;
      
      if (isPath) {
        console.log(`Attempting to hydrate by path: ${nodeIdOrPath}`);
        
        // Try to find the node ID for this path in the store
        const manifest = store.manifest;
        if (manifest && manifest.nodes) {
          for (const [id, node] of Object.entries(manifest.nodes)) {
            const nodePath = node.path || '';
            // Compare with and without leading slash
            if (nodePath === nodeIdOrPath || 
                ('/' + nodePath) === nodeIdOrPath || 
                nodePath === ('/' + nodeIdOrPath)) {
              effectiveNodeId = id;
              console.log(`Resolved path ${nodeIdOrPath} to node ID ${id}`);
              break;
            }
          }
        }
        
        // Update status for both the path and the node ID if found
        store.updateNodeLocal(nodeIdOrPath, { status: "hydrating" });
        if (effectiveNodeId !== nodeIdOrPath) {
          store.updateNodeLocal(effectiveNodeId, { status: "hydrating" });
        }
      } else {
        // It's a node ID, update its status
        store.updateNodeLocal(nodeIdOrPath, { status: "hydrating" });
      }
      
      // Call the API to hydrate the node - always use the node ID endpoint if possible
      const result = await api.post(`/artifactory/hydrate/${effectiveNodeId}`, {
        manifest_id: manifestId,
        session_id: sessionId,
        with_dependencies: withDependencies
      });
      
      // Check if the response contains an error
      if (result.data.error) {
        throw new Error(result.data.error);
      }
      
      // Update the store with the result
      if (result.data.success && result.data.artifact_id) {
        // Update both the path and node ID if they're different
        store.updateNodeLocal(nodeIdOrPath, { 
          status: "fresh", 
          artifact_id: result.data.artifact_id 
        });
        
        if (effectiveNodeId !== nodeIdOrPath) {
          store.updateNodeLocal(effectiveNodeId, { 
            status: "fresh", 
            artifact_id: result.data.artifact_id 
          });
        }
      } else if (!result.data.success) {
        // If the operation wasn't successful but didn't throw an error
        throw new Error(result.data.message || "Hydration failed without specific error");
      }
      
      return result.data;
    } catch (error) {
      // Handle errors, including store access errors
      try {
        // Try to update the store if available
        const store = useArtifactoryStore.getState();
        store.updateNodeLocal(nodeIdOrPath, { status: "ghost" });
        
        // If we resolved to a different node ID, update that too
        if (effectiveNodeId !== nodeIdOrPath) {
          store.updateNodeLocal(effectiveNodeId, { status: "ghost" });
        }
        
        // Add error metadata to the node
        store.updateNodeLocal(nodeIdOrPath, { 
          metadata: { 
            last_error: error.message || "Unknown error occurred",
            last_error_timestamp: new Date().toISOString()
          } 
        });
      } catch (storeError) {
        console.error("Error accessing store:", storeError);
      }
      
      // Re-throw the original error
      throw error;
    }
  },

  fabricateAll: async (manifestId: string, sessionId: string): Promise<any> => {
    const response = await api.post(`/artifactory/fabricate/${manifestId}`, {
      session_id: sessionId
    });
    return response.data;
  },
  
  getSessionManifest: async (sessionId: string): Promise<{ has_manifest: boolean, manifest_id: string | null }> => {
    try {
      console.log(`Fetching manifest info for session: ${sessionId}`);
      const response = await api.get(`/artifactory/session/${sessionId}/manifest`);
      console.log(`Session manifest response:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fetching session manifest for ${sessionId}:`, error);
      return { has_manifest: false, manifest_id: null };
    }
  },
  
  // Living Blueprint SP1 — read the manifest bound to a trail (session.data).
  // Re-throws on error; the caller (ChatDagCanvas) owns the null fallback.
  getSessionBlueprint: async (sessionId: string): Promise<{ manifest_id: string | null }> => {
    const r = await api.get(`/artifactory/session/${sessionId}/blueprint`);
    return r.data;
  },

  // Living Blueprint SP1 — the user's OWN manifests (owner-scoped) for the
  // summon picker. Re-throws; the modal surfaces the error inline.
  listOwnedManifests: async (): Promise<{ id: string; name: string }[]> => {
    const r = await api.get('/artifactory/manifests');
    return r.data;
  },

  // Living Blueprint SP1 — bind a manifest onto a trail. Re-throws (409 soft
  // limit / 403 non-owned manifest) so the modal can message it inline.
  bindBlueprint: async (sessionId: string, manifestId: string): Promise<{ manifest_id: string }> => {
    const r = await api.post(`/artifactory/session/${sessionId}/blueprint`, { manifest_id: manifestId });
    return r.data;
  },

  // SP-Summon — complete an agent-driven summon: the persona summon_blueprint
  // tool binds ONLY (mid-turn spine writes collide with the turn save), so the FE
  // roots the intent spine via this clean idempotent route once the bind lands.
  // Re-throws; the caller swallows (best-effort, no-op if the spine exists).
  ensureBlueprintSpine: async (sessionId: string): Promise<{ manifest_id: string; created: boolean }> => {
    const r = await api.post(`/artifactory/session/${sessionId}/blueprint/ensure-spine`);
    return r.data;
  },

  // Living Blueprint SP2b — birth an empty seedling blueprint rooting its own
  // intent spine (the general spawn_spine_from_source route, blueprint_intent
  // branch). Re-throws; the caller surfaces non-409 errors inline.
  newBlueprint: async (sessionId: string): Promise<{ manifest_id: string; source_kind: string; seedling: boolean }> => {
    const r = await api.post(`/artifactory/session/${sessionId}/spawn-spine`, { source_kind: 'blueprint_intent' });
    return r.data;
  },

  // Source Spines SP-3b — root (or re-open) a chat-spine on an EXISTING artifact.
  // Idempotent server-side (FOR-UPDATE on the (kind, source_id) tuple); re-throws
  // 403/404 so the caller can surface a tile-level error.
  spawnArtifactSpine: async (sessionId: string, artifactId: string):
    Promise<{ source_kind: string; source_id: string; spine_root_message_id: string; spine_root_created: boolean }> => {
    const r = await api.post(`/artifactory/session/${sessionId}/spawn-spine`,
      { source_kind: 'artifact', source_id: artifactId });
    return r.data;
  },

  // SP2b — rough, read-only genesis estimate (no model call). Feeds the modal.
  genesisEstimate: async (sessionId: string, intent: string): Promise<{ node_count_hint: number; token_est: number; rough: boolean }> => {
    const r = await api.post(`/artifactory/session/${sessionId}/blueprint/genesis-estimate`, { intent });
    return r.data;
  },

  // SP2b — dispatch genesis (flips germinating, runs the architect in the bg).
  // Re-throws so the modal can special-case 409 (already germinating).
  generateBlueprint: async (sessionId: string, intent: string): Promise<{ manifest_id: string; germinating: boolean }> => {
    const r = await api.post(`/artifactory/session/${sessionId}/blueprint/generate`, { intent });
    return r.data;
  },

  // SP2b — session-owned genesis poll. The node polls THIS (not GET /manifest).
  blueprintStatus: async (sessionId: string): Promise<BlueprintLiveStatus> => {
    const r = await api.get(`/artifactory/session/${sessionId}/blueprint/status`);
    return r.data;
  },

  mutateProject: async (manifestId: string, sessionId: string, intent: string): Promise<any> => {
    const response = await api.post(`/artifactory/mutate/${manifestId}`, {
      session_id: sessionId,
      intent: intent
    });
    return response.data;
  },

  getProjectHydrationStatus: async (manifestId: string): Promise<any> => {
    const response = await api.get(`/artifactory/hydration-status/${manifestId}`);
    return response.data;
  },

  getHydrationPlan: async (manifestId: string, focusNodeId?: string): Promise<any> => {
    try {
      console.log(`Requesting hydration plan for manifest ${manifestId}${focusNodeId ? ` with focus on node ${focusNodeId}` : ''}`);
      console.log(`getHydrationPlan - manifestId type: ${typeof manifestId}, value: ${manifestId}`);
      
      if (!manifestId) {
        console.error('Cannot request hydration plan: manifestId is empty or undefined');
        return { 
          error: "Missing manifest ID", 
          ordered_plan: [], 
          nodes_to_hydrate: 0 
        };
      }
      
      const url = focusNodeId 
        ? `/artifactory/hydration-plan/${manifestId}?focus_node_id=${focusNodeId}`
        : `/artifactory/hydration-plan/${manifestId}`;
      
      console.log(`Making API request to: ${url}`);
      const response = await api.get(url);
      console.log(`Hydration plan API response:`, response.data);
      
      // Ensure the response has the expected structure
      if (!response.data.ordered_plan) {
        console.warn('Hydration plan response missing ordered_plan:', response.data);
        // Add a default empty ordered_plan if missing
        return {
          ...response.data,
          ordered_plan: [],
          nodes_to_hydrate: 0
        };
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching hydration plan for manifest ${manifestId}:`, error);
      throw error;
    }
  },

  exportProject: (manifestId: string, includeStale: boolean = false, includeGhost: boolean = false): string => {
    return `/api/artifactory/export/${manifestId}?include_stale=${includeStale}&include_ghost=${includeGhost}`;
  },

  getHandoffPlugins: async (): Promise<HandoffPlugin[]> => {
    const response = await api.get('/artifactory/handoff/meta/plugins');
    return response.data;
  },

  handoffProject: (
    manifestId: string,
    tool: string,
    options: {
      includeStale?: boolean;
      includeGhost?: boolean;
      includeBuildOrder?: boolean;
      includeIntentHistory?: boolean;
    } = {}
  ): string => {
    const params = new URLSearchParams({
      tool,
      include_stale: String(options.includeStale ?? true),
      include_ghost: String(options.includeGhost ?? true),
      include_build_order: String(options.includeBuildOrder ?? true),
      include_intent_history: String(options.includeIntentHistory ?? false),
    });
    return `/api/artifactory/handoff/${manifestId}?${params.toString()}`;
  },

  previewHandoff: async (
    manifestId: string,
    tool: string,
    options: {
      includeStale?: boolean;
      includeGhost?: boolean;
      includeBuildOrder?: boolean;
      includeIntentHistory?: boolean;
    } = {}
  ): Promise<HandoffPreview> => {
    const params = new URLSearchParams({
      tool,
      include_stale: String(options.includeStale ?? true),
      include_ghost: String(options.includeGhost ?? true),
      include_build_order: String(options.includeBuildOrder ?? true),
      include_intent_history: String(options.includeIntentHistory ?? false),
    });
    const response = await api.get(
      `/artifactory/handoff/${manifestId}/preview?${params.toString()}`
    );
    return response.data as HandoffPreview;
  },

  reviewHandoff: async (
    manifestId: string,
    tool: string,
    options: {
      includeStale?: boolean;
      includeGhost?: boolean;
      includeBuildOrder?: boolean;
      includeIntentHistory?: boolean;
    } = {}
  ): Promise<HandoffReview> => {
    const params = new URLSearchParams({
      tool,
      include_stale: String(options.includeStale ?? true),
      include_ghost: String(options.includeGhost ?? true),
      include_build_order: String(options.includeBuildOrder ?? true),
      include_intent_history: String(options.includeIntentHistory ?? false),
    });
    const response = await api.post(
      `/artifactory/handoff/${manifestId}/review?${params.toString()}`
    );
    return response.data as HandoffReview;
  },
};

export interface HandoffReviewFinding {
  severity: string;
  file: string;
  issue: string;
  suggestion: string;
}

export interface HandoffReview {
  findings: HandoffReviewFinding[];
  summary: string;
}

export interface HandoffPreviewFile {
  path: string;
  size: number;
  kind: string;
  content: string | null;
}

export interface HandoffPreview {
  files: HandoffPreviewFile[];
  summary: {
    fresh: number;
    stale: number;
    ghost: number;
    hydration_pct: number;
    subagents: number;
  };
}
